import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import ReactPaginate from "react-paginate";
import ReactModal from "react-modal";
import Select from "react-select";
import { toast } from "react-toastify";
import { Loader2, Search, Filter, RefreshCw, Eye, Pencil, Trash2, Send } from "lucide-react";
import LinkedContextDialog from "../LinkedContext/LinkedContextDialog";

const isDateField = (f) => /(_at|_time)$/.test(f);
const toDate = (v) => {
    if (!v) return null;
    const s = typeof v === "string" ? v.replace(/ GMT$/, "") : v;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};
const fmt = (d) => {
    if (!d) return "-";
    const dt = new Date(String(d).replace(/ GMT$/, ""));
    const day = String(dt.getDate()).padStart(2, "0");
    const mon = dt.toLocaleString("en-AU", { month: "short" });
    const yr = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, "0");
    const mm = String(dt.getMinutes()).padStart(2, "0");
    return `${day} ${mon}, ${yr} ${hh}:${mm}`;
};

const normalizeDomain = (value) => {
    if (!value) return "";
    let s = String(value).trim().toLowerCase();
    if (s.includes("@")) s = s.split("@")[1] || s;
    s = s.replace(/^https?:\/\//, "").replace(/^www\./, "");
    s = s.split("/")[0] || s;
    return s.replace(/\.+$/, "");
};

// --- Shared react-select style (44px controls) ---
const selectStyles44 = {
    control: (p) => ({
        ...p,
        minHeight: 44,
        height: 44,
        borderColor: "#e2e8f0",
        boxShadow: "none",
        borderRadius: 12,
        "&:hover": { borderColor: "#cbd5e1" },
    }),
    valueContainer: (p) => ({ ...p, height: 44 }),
    input: (p) => ({ ...p, margin: 0, padding: 0 }),
    option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
        color: s.isSelected ? "white" : "#0f172a",
    }),
    menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const badgeForStatus = (s) => {
    const v = String(s || "").toLowerCase();
    const base = "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset";
    if (v === "sent") return `${base} bg-emerald-50 text-emerald-700 ring-emerald-600/20`;
    if (v === "pending") return `${base} bg-amber-50 text-amber-700 ring-amber-600/20`;
    if (v === "scheduled") return `${base} bg-sky-50 text-sky-700 ring-sky-600/20`;
    if (v === "failed") return `${base} bg-rose-50 text-rose-700 ring-rose-600/20`;
    if (v === "cancelled") return `${base} bg-slate-100 text-slate-700 ring-slate-600/20`;
    return `${base} bg-slate-100 text-slate-700 ring-slate-600/20`;
};

export default function EmailQueue() {
    const [rows, setRows] = useState([]);
    const DEFAULT_STATUS = { value: "pending", label: "Pending" };
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS);

    const statusOptions = [
        { value: "", label: "All statuses" },
        { value: "pending", label: "Pending" },
        { value: "scheduled", label: "Scheduled" },
        { value: "sent", label: "Sent" },
        { value: "failed", label: "Failed" },
        { value: "cancelled", label: "Cancelled" },
    ];
    const modalStatusOptions = [{ value: "pending", label: "Pending" }];

    const [currentPage, setCurrentPage] = useState(0);
    const [sortField, setSortField] = useState("relevance");
    const [sortOrder, setSortOrder] = useState("desc");

    const [modalType, setModalType] = useState(null);
    const [modalRecord, setModalRecord] = useState(null);
    const [isContextDialogOpen, setIsContextDialogOpen] = useState(false);
    const [contextLoading, setContextLoading] = useState(false);
    const [contextRecord, setContextRecord] = useState(null);
    const [contextContacts, setContextContacts] = useState([]);
    const [blacklistBusy, setBlacklistBusy] = useState(false);

    const [loading, setLoading] = useState(false);
    const [rowBusyId, setRowBusyId] = useState(null);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchQueue = async (pageToLoad = currentPage + 1) => {
        const safePage =
            typeof pageToLoad === "number" && Number.isFinite(pageToLoad)
                ? pageToLoad
                : currentPage + 1;
        try {
            setLoading(true);
            const resp = await axiosInstance.get("/queue/emailQueue", {
                params: {
                    page: safePage,
                    page_size: recordsPerPage,
                    sort_by: sortField || "relevance",
                    sort_order: sortOrder || "desc",
                    status: statusFilter.value || undefined,
                    q: searchTerm.trim() || undefined,
                },
            });
            const payload = resp?.data || {};
            setRows(Array.isArray(payload?.items) ? payload.items : []);
            setTotalPages(Number(payload?.total_pages || 1));
            setTotalRecords(Number(payload?.total || 0));
        } catch (e) {
            console.error(e);
            toast.error("Failed to fetch email queue.");
        } finally {
            setLoading(false);
        }
    };

    const recordsPerPage = 30;
    useEffect(() => {
        fetchQueue(currentPage + 1);
    }, [currentPage, sortField, sortOrder, statusFilter.value, searchTerm]);

    const pageCount = totalPages || 0;
    const displayed = rows;

    const handleSort = (field) => {
        if (sortField !== field) {
            setSortField(field);
            setSortOrder("asc");
        } else if (sortOrder === "asc") {
            setSortOrder("desc");
        } else {
            setSortField(null);
            setSortOrder("asc");
        }
    };

    const openModal = (type, record) => {
        setModalType(type);
        setModalRecord(record);
    };
    const closeModal = () => {
        setModalType(null);
        setModalRecord(null);
    };
    const handleModalChange = (e) => {
        const { name, value } = e.target;
        setModalRecord((prev) => ({ ...prev, [name]: value }));
    };

    const handleCancel = async () => {
        const id = modalRecord?.email_queue_id;
        if (!id) return;
        try {
            await axiosInstance.delete(`/queue/emailQueue/${id}`);
            toast.success("Cancelled successfully!");
            fetchQueue();
            closeModal();
        } catch {
            toast.error("Failed to cancel job.");
        }
    };

    const handleUpdate = async () => {
        const id = modalRecord?.email_queue_id;
        if (!id) return;
        try {
            await axiosInstance.put(`/queue/emailQueue/${id}`, modalRecord);
            toast.success("Updated successfully!");
            fetchQueue();
            closeModal();
        } catch {
            toast.error("Failed to update job.");
        }
    };

    const handleSend = async (email_queue_id) => {
        if (!email_queue_id) return;
        try {
            setRowBusyId(email_queue_id);
            const { data } = await axiosInstance.post(`/queue/emailQueue/sendEmail/${email_queue_id}`);
            if (data?.already_sent) {
                toast.info("Email already sent.");
            } else if (data?.bounced) {
                toast.warning("Email was attempted but bounced.");
            } else {
                toast.success("Email sent successfully!");
            }
            await fetchQueue();
            closeModal();
        } catch {
            toast.error("Failed to send email.");
        } finally {
            setRowBusyId(null);
        }
    };

    const closeContextDialog = () => {
        setIsContextDialogOpen(false);
        setContextLoading(false);
        setContextRecord(null);
        setContextContacts([]);
    };

    const openContextDialog = async (queueItem = null) => {
        const source = queueItem || modalRecord;
        const id = source?.email_queue_id;
        if (!id) return;

        setIsContextDialogOpen(true);
        setContextLoading(true);
        setContextRecord(null);
        setContextContacts([]);

        try {
            const resp = await axiosInstance.get(`/queue/emailQueue/${id}/context`);
            const payload = resp?.data || {};
            const emailQueue = payload?.email_queue || source || {};
            const domain = normalizeDomain(payload?.domain || emailQueue?.email_address || "");
            const record = payload?.record || {
                company_name: emailQueue?.company_name || "",
                domain,
            };
            const contacts = Array.isArray(payload?.related_contacts)
                ? payload.related_contacts
                : payload?.contact
                    ? [payload.contact]
                    : [];

            setContextRecord(record);
            setContextContacts(contacts);
        } catch (e) {
            console.error(e);
            toast.error("Failed to load linked context.");
        } finally {
            setContextLoading(false);
        }
    };

    const addDomainToBlacklist = async (domain) => {
        const normalized = normalizeDomain(domain);
        if (!normalized) {
            toast.error("No domain available to blacklist.");
            return;
        }
        try {
            setBlacklistBusy(true);
            await axiosInstance.post("/blacklist/blacklists", { domain: normalized });
            toast.success(`${normalized} added to blacklist.`);
        } catch (err) {
            const msg = err?.response?.data?.error || "Failed to add domain to blacklist.";
            toast.error(msg);
        } finally {
            setBlacklistBusy(false);
        }
    };

    const columns = [
        { label: "Contact", field: "contact" },
        { label: "Email", field: "email_address" },
        { label: "Company", field: "company_name" },
        { label: "Status", field: "status" },
        { label: "Relevance", field: "relevance" },
        { label: "Scheduled", field: "scheduled_time" },
        { label: "Created", field: "created_at" },
    ];

    // CHANGE: allow certain fields to span full row in the modal grid
    const renderFields = (record, editable = false) => {
        const defs = [
            { label: "Email address", name: "email_address" },
            { label: "Company name", name: "company_name" },
            // CHANGE: Email content should occupy one full row (span 2 columns on sm+)
            { label: "Email content", name: "email_content", type: "textarea", fullRow: true },
            { label: "Contact", name: "contact" },
        ];
        const common = [
            { label: "Status", name: "status", type: "select", options: modalStatusOptions },
            { label: "Relevance", name: "relevance", type: "number" },
            { label: "Scheduled time", name: "scheduled_time", readOnly: true },
            { label: "Attempts", name: "attempts", readOnly: true },
            { label: "Max attempts", name: "max_attempts", readOnly: true },
            { label: "Created at", name: "created_at", readOnly: true },
            { label: "Updated at", name: "updated_at", readOnly: true },
        ];
        const fields = [...defs, ...common];

        return fields.map((f) => {
            const val = record[f.name] ?? "";
            const isDate = ["created_at", "updated_at", "scheduled_time"].includes(f.name);
            const wrapperClass = `space-y-1 ${f.fullRow ? "sm:col-span-2" : ""}`;

            if (isDate) {
                return (
                    <div key={f.name} className={wrapperClass}>
                        <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                        <div className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 flex items-center text-sm text-slate-700">
                            {fmt(val)}
                        </div>
                    </div>
                );
            }

            if (editable && !f.readOnly) {
                if (f.type === "textarea") {
                    return (
                        <div key={f.name} className={wrapperClass}>
                            <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                            <textarea
                                name={f.name}
                                value={val}
                                onChange={handleModalChange}
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 min-h-[120px]"
                            />
                        </div>
                    );
                }
                if (f.type === "select") {
                    return (
                        <div key={f.name} className={wrapperClass}>
                            <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                            <Select
                                name={f.name}
                                value={(f.options || []).find((o) => o.value === val)}
                                onChange={(opt) => setModalRecord((prev) => ({ ...prev, [f.name]: opt?.value }))}
                                options={f.options || []}
                                classNamePrefix="react-select"
                                styles={selectStyles44}
                            />
                        </div>
                    );
                }
                return (
                    <div key={f.name} className={wrapperClass}>
                        <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                        <input
                            name={f.name}
                            type={f.type || "text"}
                            value={val}
                            onChange={handleModalChange}
                            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400"
                        />
                    </div>
                );
            }

            return (
                <div key={f.name} className={wrapperClass}>
                    <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                    <div
                        className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap break-words"
                        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                    >
                        {String(val)}
                    </div>
                </div>
            );
        });
    };

    const cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm";
    const iconBtn =
        "inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:opacity-50";
    const subtleBtn =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:opacity-50";
    const dangerBtn =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-rose-700 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-800 focus:outline-none focus:ring-2 focus:ring-rose-600/30 disabled:opacity-50";
    const warnBtn =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-amber-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500/30 disabled:opacity-50";

    return (
        <>
            {/* Controls */}
            <div className={`${cardClass} p-4`}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search company or email..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(0);
                                }}
                                className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/20"
                            />
                        </div>

                        <div className="min-w-[220px]">
                            <div className="flex items-center gap-2">
                                <Filter className="h-4 w-4 text-slate-500" />
                                <div className="flex-1">
                                    <Select
                                        value={statusFilter}
                                        onChange={(v) => {
                                            setStatusFilter(v || DEFAULT_STATUS);
                                            setCurrentPage(0);
                                        }}
                                        options={statusOptions}
                                        classNamePrefix="react-select"
                                        placeholder="Status"
                                        styles={selectStyles44}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <button onClick={() => fetchQueue(currentPage + 1)} disabled={loading} className={subtleBtn} aria-busy={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Refresh
                    </button>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm text-slate-600">
                        {searchTerm || statusFilter.value ? `Showing ${displayed.length} of ${totalRecords} total` : `Total records: ${totalRecords}`}
                    </div>

                    <div className="text-xs text-slate-500">
                        Sort: <span className="font-semibold text-slate-700">{sortField ? `${sortField} (${sortOrder})` : "none"}</span>
                    </div>
                </div>
            </div>

            {/* Desktop table */}
            <div className={`mt-4 hidden md:block ${cardClass} overflow-hidden`}>
                <div className="overflow-x-auto">
                    <table className="w-full table-auto border-collapse">
                        <thead className="bg-slate-50">
                            <tr className="border-b border-slate-200">
                                {columns.map((col) => (
                                    <th
                                        key={col.field}
                                        onClick={() => {
                                            setCurrentPage(0);
                                            handleSort(col.field);
                                        }}
                                        className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wide text-slate-700 cursor-pointer select-none hover:bg-slate-100"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span>{col.label}</span>
                                            <span className="text-slate-400">
                                                {sortField === col.field ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                                            </span>
                                        </div>
                                    </th>
                                ))}
                                <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wide text-slate-700">
                                    Actions
                                </th>
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100">
                            {displayed.length ? (
                                displayed.map((rec) => (
                                    <tr
                                        key={rec.email_queue_id}
                                        className="hover:bg-slate-50 cursor-pointer"
                                        onClick={() => openModal("view", rec)}
                                    >
                                        <td className="px-4 py-3 text-sm text-slate-800">{rec.contact}</td>
                                        <td className="px-4 py-3 text-sm text-slate-800 max-w-[260px] truncate">{rec.email_address || "-"}</td>
                                        <td className="px-4 py-3 text-sm text-slate-800 max-w-[240px] truncate">{rec.company_name || "-"}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className={badgeForStatus(rec.status)}>{rec.status || "n/a"}</span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-800">{rec.relevance ?? "-"}</td>
                                        <td className="px-4 py-3 text-sm text-slate-700">{fmt(rec.scheduled_time)}</td>
                                        <td className="px-4 py-3 text-sm text-slate-700">{fmt(rec.created_at)}</td>

                                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center gap-2">
                                                <button className={iconBtn} title="Linked context" aria-label="Linked context" onClick={() => openContextDialog(rec)}>
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button className={iconBtn} title="Update" aria-label="Update" onClick={() => openModal("update", rec)}>
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                {/* <button
                                                    className={`${iconBtn} border-rose-200 text-rose-700 hover:bg-rose-50`}
                                                    title="Cancel"
                                                    aria-label="Cancel"
                                                    onClick={() => openModal("cancel", rec)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button> */}
                                                <button
                                                    className={`${iconBtn} border-emerald-200 text-emerald-700 hover:bg-emerald-50`}
                                                    title="Send email"
                                                    aria-label="Send email"
                                                    onClick={() => handleSend(rec.email_queue_id)}
                                                    disabled={rowBusyId === rec.email_queue_id || (rec.status || "").toLowerCase() === "sent"}
                                                >
                                                    {rowBusyId === rec.email_queue_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td className="px-4 py-10 text-center text-sm text-slate-500" colSpan={columns.length + 1}>
                                        {loading ? (
                                            <span className="inline-flex items-center gap-2">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Loading...
                                            </span>
                                        ) : (
                                            "No data"
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Mobile cards */}
            <div className="mt-4 block md:hidden space-y-3">
                {displayed.length ? (
                    displayed.map((rec) => (
                        <div
                            key={rec.email_queue_id}
                            className={`${cardClass} p-4`}
                            onClick={() => openModal("view", rec)}
                            role="button"
                            tabIndex={0}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-bold text-slate-900">{rec.contact}</div>
                                    <div className="text-sm text-slate-900 truncate">{rec.company_name || "—"}</div>
                                    <div className="mt-1 text-sm text-slate-700 truncate">{rec.email_address || "—"}</div>
                                </div>
                                <span className={badgeForStatus(rec.status)}>{rec.status || "n/a"}</span>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                                <div>
                                    <div className="text-xs font-semibold text-slate-500">Relevance</div>
                                    <div className="text-slate-800">{rec.relevance ?? "—"}</div>
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-slate-500">Scheduled</div>
                                    <div className="text-slate-800">{fmt(rec.scheduled_time)}</div>
                                </div>
                                <div className="col-span-2">
                                    <div className="text-xs font-semibold text-slate-500">Created</div>
                                    <div className="text-slate-800">{fmt(rec.created_at)}</div>
                                </div>
                            </div>

                            <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <button className={iconBtn} onClick={() => openContextDialog(rec)} title="Linked context" aria-label="Linked context">
                                    <Eye className="h-4 w-4" />
                                </button>
                                <button className={iconBtn} onClick={() => openModal("update", rec)} title="Update" aria-label="Update">
                                    <Pencil className="h-4 w-4" />
                                </button>
                                {/* <button
                                    className={`${iconBtn} border-rose-200 text-rose-700 hover:bg-rose-50`}
                                    onClick={() => openModal("cancel", rec)}
                                    title="Cancel"
                                    aria-label="Cancel"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button> */}
                                <button
                                    className={`${iconBtn} border-emerald-200 text-emerald-700 hover:bg-emerald-50`}
                                    onClick={() => handleSend(rec.email_queue_id)}
                                    title="Send email"
                                    aria-label="Send email"
                                    disabled={rowBusyId === rec.email_queue_id || (rec.status || "").toLowerCase() === "sent"}
                                >
                                    {rowBusyId === rec.email_queue_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className={`${cardClass} p-8 text-center text-sm text-slate-500`}>
                        {loading ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading...
                            </span>
                        ) : (
                            "No data"
                        )}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
                <ReactPaginate
                    previousLabel={<span className="px-3 py-1">← Prev</span>}
                    nextLabel={<span className="px-3 py-1">Next →</span>}
                    pageCount={pageCount}
                    onPageChange={({ selected }) => setCurrentPage(selected)}
                    containerClassName="flex justify-center items-center mt-6 gap-2"
                    pageClassName="px-3 py-1 border border-slate-200 rounded-xl bg-white text-slate-700 hover:bg-slate-50"
                    activeClassName="!bg-slate-900 !text-white !border-slate-900 shadow-sm"
                    disabledClassName="opacity-50 cursor-not-allowed"
                    breakClassName="px-3 py-1 text-slate-500"
                    renderOnZeroPageCount={null}
                />
            )}

            {/* View Modal */}
            <ReactModal
                isOpen={modalType === "view"}
                onRequestClose={closeModal}
                className="bg-white p-6 rounded-2xl shadow-xl max-w-4xl w-[95vw] max-h-[85vh] overflow-y-auto mx-auto my-10 border border-slate-200"
                overlayClassName="fixed inset-0 bg-slate-900/40 flex justify-center items-center p-4 z-50"
                ariaHideApp={false}
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-xl font-extrabold text-slate-900">Queue item</h2>
                        <p className="mt-1 text-sm text-slate-600">Read-only details.</p>
                    </div>
                    <button
                        onClick={closeModal}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                        aria-label="Close"
                        title="Close"
                    >
                        ✕
                    </button>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">{modalRecord && renderFields(modalRecord, false)}</div>

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button onClick={() => openContextDialog(modalRecord)} className="bg-slate-900 hover:bg-slate-800 text-white inline-flex h-11 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20">
                        <Eye className="h-4 w-4" />
                        Linked Context
                    </button>
                    <button onClick={closeModal} className={subtleBtn}>
                        Close
                    </button>
                </div>
            </ReactModal>

            {/* Update Modal */}
            <ReactModal
                isOpen={modalType === "update"}
                onRequestClose={closeModal}
                className="bg-white p-6 rounded-2xl shadow-xl max-w-4xl w-[95vw] max-h-[85vh] overflow-y-auto mx-auto my-10 border border-slate-200"
                overlayClassName="fixed inset-0 bg-slate-900/40 flex justify-center items-center p-4 z-50"
                ariaHideApp={false}
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-xl font-extrabold text-slate-900">Update queue item</h2>
                        <p className="mt-1 text-sm text-slate-600">Editable fields only.</p>
                    </div>
                    <button
                        onClick={closeModal}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                        aria-label="Close"
                        title="Close"
                    >
                        ✕
                    </button>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">{modalRecord && renderFields(modalRecord, true)}</div>

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button onClick={closeModal} className={subtleBtn}>
                        Cancel
                    </button>
                    <button onClick={handleUpdate} className={warnBtn}>
                        <Pencil className="h-4 w-4" />
                        Update
                    </button>
                </div>
            </ReactModal>

            {/* Cancel Modal */}
            <ReactModal
                isOpen={modalType === "cancel"}
                onRequestClose={closeModal}
                className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-[95vw] mx-auto my-auto border border-slate-200"
                overlayClassName="fixed inset-0 bg-slate-900/40 flex justify-center items-center p-4 z-50"
                ariaHideApp={false}
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-xl font-extrabold text-slate-900">Cancel job</h2>
                        <p className="mt-1 text-sm text-slate-600">This will delete the queue job. This action cannot be undone.</p>
                    </div>
                    <button
                        onClick={closeModal}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                        aria-label="Close"
                        title="Close"
                    >
                        ✕
                    </button>
                </div>

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button onClick={closeModal} className={subtleBtn}>
                        Cancel
                    </button>
                    <button onClick={handleCancel} className={dangerBtn}>
                        <Trash2 className="h-4 w-4" />
                        Delete
                    </button>
                </div>
            </ReactModal>

            <LinkedContextDialog
                isOpen={isContextDialogOpen}
                onClose={closeContextDialog}
                title="Email Context"
                loading={contextLoading}
                record={contextRecord}
                contacts={contextContacts}
                onAddBlacklist={addDomainToBlacklist}
                blacklistBusy={blacklistBusy}
            />
        </>
    );
}
